import { useStore } from '../../../store/store';
import { useLocalObservable, observer } from 'mobx-react';
import { Link } from 'react-router';
import { paths } from '../../../utils/constants/routes';
import { LOGIN_FIELDS, getErrorFields, trimStateFields } from '../../../utils/constants/fields';
import { useEffect } from 'react';
import SignLayout from '../../../components/layouts/SignLayout';
import Input from '../../../components/inputs/Input';
import OtpInput from '../../../components/inputs/OtpInput';
import { mapFieldsToState } from '../../../utils/utils';
import Signup from '../Signup';
import SupportedByBlock from '../../../components/supportedByBlock';

const OTP_LENGTH = 6;

const Login = observer(() => {
  const { authStore } = useStore();

  const state = useLocalObservable(() => ({
    fields: mapFieldsToState(LOGIN_FIELDS),
    setFieldValue: (field = {}, value) => {
      state.fields[field.NAME] = value;
    },
    onSubmitErrorState: false,
    setOnSubmitErrorState: (value = false) => (state.onSubmitErrorState = value),
    // TD-1976 Phase 5: holds the 6-digit TOTP code entered on the MFA step.
    // Kept local rather than added to LOGIN_FIELDS — the OTP is not a free-text
    // field validated against the password form; it's a separate step with its
    // own validation (regex `^\d{6}$`) and its own submit handler.
    mfaCode: '',
    setMfaCode: (value = '') => {
      state.mfaCode = value;
    },
    get validationFields() {
      return getErrorFields(Object.values(LOGIN_FIELDS), state.fields);
    }
  }));

  useEffect(() => {
    return () => {
      authStore.cleanUpAuthPage();
    };
  }, [authStore]);

  useEffect(() => {
    document.title = `transact.digital GmbH - AI-powered private market intelligence`;
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();

    trimStateFields(state.fields);
    if (state.validationFields.invalidFields.length) {
      if (!state.onSubmitErrorState) {
        state.setOnSubmitErrorState(true);
      }
      return;
    }

    authStore.login(fields[LOGIN_FIELDS.EMAIL.NAME], fields[LOGIN_FIELDS.PASSWORD.NAME]);
  };

  const onSubmitMfa = (e) => {
    e?.preventDefault?.();
    if (state.mfaCode.length !== OTP_LENGTH) return;
    authStore.submitLoginMfaCode(state.mfaCode);
  };

  const onBackToSignIn = (e) => {
    e?.preventDefault?.();
    state.setMfaCode('');
    authStore.cancelLoginMfa();
  };

  // const linkedinQueryParams = {
  //   identity_provider: 'linkedin',
  //   redirect_uri: window.location.origin + '?authenticate',
  //   response_type: 'CODE',
  //   client_id: import.meta.env.VITE_COGNITO_CLIENT_ID,
  //   scope: 'aws.cognito.signin.user.admin email openid phone profile'
  // };
  // const linkedinLoginUrl =
  //   import.meta.env.VITE_COGNITO_DOMAIN +
  //   '/oauth2/authorize?' +
  //   new URLSearchParams(linkedinQueryParams);

  const { setFieldValue, fields } = state;
  if (authStore.showLoginVerifyScreen) {
    return <Signup />;
  }

  // TD-1976 Phase 5 — Login TOTP challenge step.
  // Reached after `authStore.login()` triggers Cognito's `totpRequired` callback.
  // The password was correct; we just need a second factor. We keep the user on
  // this page (no route change) and submit the code via the same CognitoUser
  // instance the AuthStore captured.
  if (authStore.loginMfaPending) {
    const hasError = !!authStore.authErrors.messages.length;
    const isSubmitDisabled =
      state.mfaCode.length !== OTP_LENGTH || authStore.isAuthenticating;

    return (
      <SignLayout>
        <div className="login-wrapper">
          <form onSubmit={onSubmitMfa}>
            <h2>Two-factor authentication</h2>
            <p className="lede">
              Enter the 6-digit code from your authenticator app to finish signing in as{' '}
              <b>{authStore.loginMfaEmail}</b>.
            </p>

            <div className="form-group">
              <label htmlFor="mfa-0">Authentication code</label>
              <OtpInput
                length={OTP_LENGTH}
                value={state.mfaCode}
                onChange={state.setMfaCode}
                onComplete={(code) => state.setMfaCode(code)}
                error={hasError}
                disabled={authStore.isAuthenticating}
                autoFocus
                name="mfa"
              />
              <div className="small mt-2">Codes refresh every 30 seconds.</div>
            </div>

            {authStore.authErrors.messages.map((m, idx) => (
              <div className={m.isValid ? 'success' : 'error'} key={`mfa-err-${idx}`}>
                {m.msg}
              </div>
            ))}

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={isSubmitDisabled}>
              {authStore.isAuthenticating ? 'Loading...' : 'Verify & sign in'}
            </button>

            <div className="row small mfa-help-row">
              {/* "Lost access" routes to /en/mfa-recovery — a UI-only page that
                  walks the user through self-recovery by re-adding the saved
                  setup key on a new authenticator app. Rendered as a real
                  react-router <Link> (renders <a href>) so a click does not
                  submit the surrounding form. "Back to sign in" stays a
                  <button> (no navigation target) per Sonar S6844. */}
              <Link to={paths.MFA_RECOVERY} className="link help-link">
                Lost access to your authenticator?
              </Link>
              <button type="button" className="link help-link" onClick={onBackToSignIn}>
                Back to sign in
              </button>
            </div>
          </form>
          <SupportedByBlock />
        </div>
      </SignLayout>
    );
  }

  return (
    <SignLayout>
      <div className="login-wrapper">
        <form onSubmit={onSubmit}>
          <Input
            {...{
              field: LOGIN_FIELDS.EMAIL,
              value: fields[LOGIN_FIELDS.EMAIL.NAME],
              setFieldValue
            }}
          />
          <Input
            {...{
              field: LOGIN_FIELDS.PASSWORD,
              value: fields[LOGIN_FIELDS.PASSWORD.NAME],
              setFieldValue
            }}
          />
          <Link className="link" to={paths.FORGOTTEN_PASSWORD}>
            Forgot Password?
          </Link>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={
              authStore.isAuthenticating ||
              state.validationFields.invalidFields.filter((f) => !f.isOnSubmit).length
            }>
            {authStore.isAuthenticating ? 'Loading...' : 'Login'}
          </button>
          {authStore.authError && <div className="error">{authStore.authError}</div>}
          {!!state.validationFields.invalidFields.filter((f) => !f.isOnSubmit).length &&
            state.validationFields.messages
              .filter((m) => !m.isOnSubmit)
              .map((m, idx) => (
                <div className={m.isValid ? 'success' : 'error'} key={idx}>
                  {m.msg}
                </div>
              ))}
          {authStore.authErrors.messages.map((m, idx) => (
            <div className={m.isValid ? 'success' : 'error'} key={idx}>
              {m.msg}
            </div>
          ))}

          <div className="text-divider">or</div>

          {/* no LI login for now */}
          {/* <div className="linkedin-wrapper">
            <a href={linkedinLoginUrl}>Login with LinkedIn</a>
          </div> */}
          <Link to={paths.SIGNUP} className="btn btn-outline btn-block">
            New here? Sign up now
          </Link>
        </form>
        <SupportedByBlock />
      </div>
    </SignLayout>
  );
});

export default Login;
